import { useState, useEffect } from 'react';
import { Clock, CheckCircle, AlertCircle, FileText, Send, Edit2, X, ChevronDown, ChevronUp, Check, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import DateFilter from './DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriversTimesheetsManager = ({ isFinance = false }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(isFinance ? 'todo' : 'active');
  const [activeItems, setActiveItems] = useState([]);
  const [todoItems, setTodoItems] = useState([]);
  const [queriedItems, setQueriedItems] = useState([]);
  const [approvedItems, setApprovedItems] = useState([]);
  const [allApprovedItems, setAllApprovedItems] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });
  const [expandedTimesheets, setExpandedTimesheets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [showWarehouseDayEdit, setShowWarehouseDayEdit] = useState(false);
  const [editingWarehouseDay, setEditingWarehouseDay] = useState(null);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [showWarehouseApproval, setShowWarehouseApproval] = useState(false);
  const [approvingTimesheet, setApprovingTimesheet] = useState(null);
  const [approvalSettings, setApprovalSettings] = useState({
    overtime_type: 'toil', // 'paid' or 'toil'
    toil_to_use: 0
  });
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submittingTimesheet, setSubmittingTimesheet] = useState(null);
  const [overtimeSplit, setOvertimeSplit] = useState({ paid: 0, toil: 0 });

  // Helper function to get week shading class based on week number within timesheet
  const getWeekShadeClass = (entries, currentIndex, dateField = 'date') => {
    if (!entries || entries.length === 0 || currentIndex < 0) return '';
    
    const currentDate = new Date(entries[currentIndex][dateField]);
    const firstDate = new Date(entries[0][dateField]);
    
    // Calculate how many weeks have passed since the first entry
    const daysDiff = Math.floor((currentDate - firstDate) / (1000 * 60 * 60 * 24));
    const weekIndex = Math.floor(daysDiff / 7);
    
    // Alternate shading: even weeks no shade, odd weeks shaded
    return weekIndex % 2 === 1 ? 'bg-gray-50' : '';
  };

  useEffect(() => {
    fetchAllData();
  }, [isFinance]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseEndpoint = isFinance ? '/api/timesheets/drivers/finance' : '/api/timesheets/drivers/manager';
      
      // Fetch all tabs data in parallel
      const requests = [
        fetch(`${BACKEND_URL}${baseEndpoint}/to-do`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}${baseEndpoint}/queried`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}${baseEndpoint}/approved`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ];

      // For managers, also fetch active (pending) timesheets
      if (!isFinance) {
        requests.push(
          fetch(`${BACKEND_URL}${baseEndpoint}/active`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );
      }

      const responses = await Promise.all(requests);
      const [todoRes, queriedRes, approvedRes, activeRes] = responses;

      if (todoRes.ok) {
        const data = await todoRes.json();
        setTodoItems(data);
      }
      
      if (queriedRes.ok) {
        const data = await queriedRes.json();
        setQueriedItems(data);
      }
      
      if (approvedRes.ok) {
        const data = await approvedRes.json();
        setAllApprovedItems(data);
        applyDateFilter(data, dateFilter);
      }

      if (activeRes && activeRes.ok) {
        const data = await activeRes.json();
        setActiveItems(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = (data, filter) => {
    if (!filter.start || !filter.end) {
      setApprovedItems(data);
      return;
    }

    const filtered = data.filter(timesheet => {
      const timesheetEnd = timesheet.end_date;
      return timesheetEnd >= filter.start && timesheetEnd <= filter.end;
    });
    setApprovedItems(filtered);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    applyDateFilter(allApprovedItems, filter);
  };

  const toggleTimesheetExpand = (timesheetId) => {
    setExpandedTimesheets(prev => ({ ...prev, [timesheetId]: !prev[timesheetId] }));
  };

  const handleSubmitToFinance = async (scheduleIds) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ schedule_ids: scheduleIds })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchAllData();
      } else {
        toast.error('Failed to submit timesheets');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit timesheets');
    }
  };

  const openSubmitDialog = (timesheet) => {
    setSubmittingTimesheet(timesheet);
    const overtime = timesheet.overtime_hours || 0;
    setOvertimeSplit({ paid: 0, toil: overtime });
    setShowSubmitDialog(true);
  };

  const handleSubmitWarehouseTimesheet = async () => {
    if (!submittingTimesheet) return;

    const totalSplit = overtimeSplit.paid + overtimeSplit.toil;
    const expectedTotal = submittingTimesheet.overtime_hours || 0;

    if (Math.abs(totalSplit - expectedTotal) > 0.01) {
      toast.error(`Overtime split must equal ${expectedTotal}h (currently ${totalSplit}h)`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${submittingTimesheet.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overtime_split: overtimeSplit
        })
      });

      if (res.ok) {
        toast.success('Timesheet submitted to finance successfully');
        setShowSubmitDialog(false);
        setSubmittingTimesheet(null);
        fetchAllData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit timesheet');
      }
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      toast.error('Failed to submit timesheet');
    }
  };

  const toggleSelectTimesheet = (timesheetId) => {
    setSelectedTimesheets(prev => 
      prev.includes(timesheetId) 
        ? prev.filter(id => id !== timesheetId)
        : [...prev, timesheetId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTimesheets.length === activeItems.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(activeItems.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTimesheets.length === 0) {
      toast.error('No timesheets selected');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete ${selectedTimesheets.length} timesheet(s)?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const deletePromises = selectedTimesheets.map(timesheetId =>
        fetch(`${BACKEND_URL}/api/timesheets/generated/${timesheetId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount > 0) {
        toast.success(`${successCount} timesheet(s) deleted successfully`);
        setSelectedTimesheets([]);
        fetchAllData();
      } else {
        toast.error('Failed to delete timesheets');
      }
    } catch (error) {
      console.error('Error deleting timesheets:', error);
      toast.error('Failed to delete timesheets');
    }
  };

  const handleEditEntry = async () => {
    if (!editingEntry || !selectedTimesheet) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${selectedTimesheet.id}/edit-entry`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingEntry)
      });

      if (res.ok) {
        toast.success('Entry updated successfully');
        setShowEditDialog(false);
        setEditingEntry(null);
        fetchAllData();
      } else {
        toast.error('Failed to update entry');
      }
    } catch (error) {
      console.error('Error editing entry:', error);
      toast.error('Failed to update entry');
    }
  };

  const handleApprove = async (timesheetId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${timesheetId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Timesheet approved successfully');
        fetchAllData();
      } else {
        toast.error('Failed to approve timesheet');
      }
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve timesheet');
    }
  };

  const handleReject = async (timesheetId, reason) => {
    if (!reason || !reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${timesheetId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejection_reason: reason })
      });

      if (res.ok) {
        toast.success('Timesheet rejected and sent back for revision');
        fetchAllData();
      } else {
        toast.error('Failed to reject timesheet');
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject timesheet');
    }
  };

  const handleResubmit = async (timesheetId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${timesheetId}/resubmit`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Timesheet resubmitted to finance');
        fetchAllData();
      } else {
        toast.error('Failed to resubmit timesheet');
      }
    } catch (error) {
      console.error('Error resubmitting:', error);
      toast.error('Failed to resubmit timesheet');
    }
  };

  const handleDeleteTimesheet = async (timesheetId, employeeName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the timesheet for ${employeeName}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${timesheetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Timesheet deleted successfully');
        fetchAllData();
      } else {
        const error = await res.json().catch(() => ({ message: 'Failed to delete timesheet' }));
        toast.error(error.message || 'Failed to delete timesheet');
      }
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      toast.error('Failed to delete timesheet');
    }
  };

  const renderScheduleCard = (schedule) => (
    <div key={schedule.id} className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold">{schedule.employee_name}</h3>
          <p className="text-sm text-gray-500">{schedule.department}</p>
          <p className="text-sm text-gray-600 mt-1">
            {new Date(schedule.start_date).toLocaleDateString()} - {new Date(schedule.end_date).toLocaleDateString()}
          </p>
        </div>
        <Button
          onClick={() => handleSubmitToFinance([schedule.id])}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit to Finance
        </Button>
      </div>
      <div className="text-sm text-gray-600">
        <p>Scheduled: {schedule.scheduled_start_time} - {schedule.scheduled_finish_time}</p>
      </div>
    </div>
  );

  const handleEditWarehouseDay = async () => {
    if (!editingWarehouseDay || !selectedTimesheet) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${selectedTimesheet.id}/edit-day`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: editingWarehouseDay.date,
          clock_in: editingWarehouseDay.clock_in,
          clock_out: editingWarehouseDay.clock_out
        })
      });

      if (!response.ok) throw new Error('Failed to update day');

      toast.success('Day updated successfully');
      setShowWarehouseDayEdit(false);
      setEditingWarehouseDay(null);
      
      // Refresh data
      fetchAllData();
    } catch (error) {
      console.error('Error updating day:', error);
      toast.error('Failed to update day');
    }
  };

  const openWarehouseDayEdit = (timesheet, day) => {
    setSelectedTimesheet(timesheet);
    setEditingWarehouseDay({
      date: day.date,
      clock_in: day.clock_in || '',
      clock_out: day.clock_out || '',
      worked_hours: day.worked_hours,
      payable_hours: day.payable_hours
    });
    setShowWarehouseDayEdit(true);
  };

  const viewEditHistory = (timesheet) => {
    setEditHistory(timesheet.edit_history || []);
    setShowEditHistory(true);
  };

  const openWarehouseApproval = (timesheet) => {
    setApprovingTimesheet(timesheet);
    setApprovalSettings({
      overtime_type: 'toil', // Default to TOIL
      toil_to_use: 0
    });
    setShowWarehouseApproval(true);
  };

  const handleWarehouseApprove = async () => {
    if (!approvingTimesheet) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timesheets/drivers/${approvingTimesheet.id}/warehouse-approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overtime_type: approvalSettings.overtime_type,
          toil_to_use: approvalSettings.toil_to_use
        })
      });

      if (!response.ok) throw new Error('Failed to approve timesheet');

      toast.success('Timesheet approved successfully');
      setShowWarehouseApproval(false);
      setApprovingTimesheet(null);
      
      // Refresh data
      fetchAllData();
    } catch (error) {
      console.error('Error approving timesheet:', error);
      toast.error('Failed to approve timesheet');
    }
  };

  const renderTimesheetCard = (timesheet, showActions = false, showResubmit = false, showSubmit = false, showCheckbox = false) => {
    const isExpanded = expandedTimesheets[timesheet.id];
    const isSelected = selectedTimesheets.includes(timesheet.id);

    return (
      <div key={timesheet.id} className="bg-white rounded-xl shadow-md p-6">
        {/* Collapsed Header */}
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              {showCheckbox && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelectTimesheet(timesheet.id)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                />
              )}
              <div>
                <h3 className="text-lg font-semibold">{timesheet.employee_name}</h3>
                <p className="text-sm text-gray-500">{timesheet.department}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                timesheet.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                timesheet.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                timesheet.status === 'queried' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {timesheet.status === 'pending' ? 'Active' :
                 timesheet.status === 'submitted' ? 'Submitted' :
                 timesheet.status === 'queried' ? 'Queried' : 'Approved'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {new Date(timesheet.period_start || timesheet.start_date).toLocaleDateString()} - {new Date(timesheet.period_end || timesheet.end_date).toLocaleDateString()}
            </p>
            {timesheet.rejection_reason && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-700"><strong>Rejection reason:</strong> {timesheet.rejection_reason}</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {showSubmit && (
              <Button
                onClick={() => openSubmitDialog(timesheet)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit to Finance
              </Button>
            )}
            {showResubmit && (
              <Button
                onClick={() => handleResubmit(timesheet.id)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Resubmit
              </Button>
            )}
            {showActions && (
              <div className="flex gap-2">
                <Button
                  onClick={() => timesheet.daily_entries ? openWarehouseApproval(timesheet) : handleApprove(timesheet.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => {
                    const reason = prompt('Reason for rejection:');
                    if (reason) {
                      handleReject(timesheet.id, reason);
                    }
                  }}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}
            <Button
              onClick={() => handleDeleteTimesheet(timesheet.id, timesheet.employee_name)}
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => toggleTimesheetExpand(timesheet.id)}
              variant="outline"
              size="sm"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  View Details
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t">

      {/* Warehouse Timesheets (daily_entries) */}
      {timesheet.daily_entries && timesheet.daily_entries.length > 0 && (
        <>
          {/* Edit History Button */}
          {timesheet.edit_history && timesheet.edit_history.length > 0 && (
            <div className="mb-4">
              <Button
                onClick={() => viewEditHistory(timesheet)}
                variant="outline"
                size="sm"
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                <Clock className="w-4 h-4 mr-2" />
                View Edit History ({timesheet.edit_history.length})
              </Button>
            </div>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Clock In</th>
                  <th className="px-2 py-2 text-left">Clock Out</th>
                  <th className="px-2 py-2 text-right">Worked</th>
                  <th className="px-2 py-2 text-right">Break</th>
                  <th className="px-2 py-2 text-right">Payable</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  {!isFinance && (activeTab === 'active' || activeTab === 'todo') && <th className="px-2 py-2">Edit</th>}
                </tr>
              </thead>
              <tbody>
                {timesheet.daily_entries.map((day, idx) => (
                  <tr key={idx} className={`border-t ${getWeekShadeClass(timesheet.daily_entries, idx, 'date')} hover:bg-blue-50`}>
                    <td className="px-2 py-2">
                      {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </td>
                    <td className="px-2 py-2">
                      {day.sessions && day.sessions.length > 0 ? (
                        <div className="space-y-0.5">
                          {day.sessions.map((session, idx) => (
                            <div key={idx} className="text-sm">{session.clock_in}</div>
                          ))}
                        </div>
                      ) : (day.clock_in || '-')}
                    </td>
                    <td className="px-2 py-2">
                      {day.sessions && day.sessions.length > 0 ? (
                        <div className="space-y-0.5">
                          {day.sessions.map((session, idx) => (
                            <div key={idx} className="text-sm">{session.clock_out}</div>
                          ))}
                        </div>
                      ) : (day.clock_out || '-')}
                    </td>
                    <td className="px-2 py-2 text-right">{day.worked_hours || '0'}h</td>
                    <td className="px-2 py-2 text-right text-red-600">
                      {day.break_deduction > 0 ? `-${day.break_deduction}h` : '-'}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-green-600">{day.payable_hours || '0'}h</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {day.status && day.status.split(' | ').map((statusPart, idx) => (
                          <span key={idx} className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                            statusPart === 'Late Start' ? 'bg-red-100 text-red-700' :
                            statusPart === 'Early Finish' ? 'bg-yellow-100 text-yellow-700' :
                            statusPart === 'Late Finish' ? 'bg-blue-100 text-blue-700' :
                            statusPart === 'Worked' ? 'bg-green-100 text-green-700' :
                            statusPart === 'Holiday' ? 'bg-teal-100 text-teal-700' :
                            statusPart === 'Sickness' ? 'bg-orange-100 text-orange-700' :
                            statusPart === 'Absent' ? 'bg-red-100 text-red-700' :
                            statusPart === 'Incomplete' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {statusPart}
                          </span>
                        ))}
                      </div>
                    </td>
                    {!isFinance && (activeTab === 'active' || activeTab === 'todo') && day.editable !== false && (
                      <td className="px-2 py-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openWarehouseDayEdit(timesheet, day)}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Warehouse Summary */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-3">Period Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              <div>
                <p className="text-gray-600 text-xs">Contracted Hours</p>
                <p className="font-bold text-lg">{timesheet.contracted_hours || '0'}h</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs">Worked Hours</p>
                <p className="font-bold text-lg text-green-600">{timesheet.total_worked_hours || '0'}h</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs">Payable Hours</p>
                <p className="font-bold text-lg text-teal-600">{timesheet.total_payable_hours || '0'}h</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs">Overtime</p>
                <p className="font-bold text-lg text-blue-600">{timesheet.overtime_hours || '0'}h</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs">Unpaid Hours</p>
                <p className="font-bold text-lg text-red-600">{timesheet.total_unpaid_hours || '0'}h</p>
              </div>
              <div>
                <p className="text-gray-600 text-xs">TOIL Balance</p>
                <p className="font-bold text-lg text-purple-600">{timesheet.toil_balance || '0'}h</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Entries Table (Old System) */}
      {timesheet.entries && timesheet.entries.length > 0 && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Clock In</th>
                  <th className="px-2 py-2 text-left">Clock Out</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-right">Sch.Hrs</th>
                  <th className="px-2 py-2 text-right">Raw Hrs</th>
                  <th className="px-2 py-2 text-right">Paid Brk</th>
                  <th className="px-2 py-2 text-right">Unpaid Brk</th>
                  <th className="px-2 py-2 text-right">Worked Hrs</th>
                  <th className="px-2 py-2 text-right">O/T</th>
                  {!isFinance && activeTab === 'todo' && <th className="px-2 py-2">Edit</th>}
                </tr>
              </thead>
              <tbody>
                {timesheet.entries.map((entry, idx) => (
                  <tr key={idx} className={`border-t ${getWeekShadeClass(timesheet.entries, idx, 'date')} hover:bg-blue-50`}>
                    <td className="px-2 py-2">{new Date(entry.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                    <td className="px-2 py-2">{entry.actual_clock_in || '-'}</td>
                    <td className="px-2 py-2">{entry.actual_clock_out || '-'}</td>
                    <td className="px-2 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        entry.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        entry.status === 'late_in_progress' ? 'bg-orange-100 text-orange-700' :
                        entry.status === 'finished' ? 'bg-green-100 text-green-700' :
                        entry.status === 'finished_early' ? 'bg-teal-100 text-teal-700' :
                        entry.status === 'finished_late' ? 'bg-yellow-100 text-yellow-700' :
                        entry.status === 'missing' ? 'bg-red-100 text-red-700' :
                        entry.status === 'sick' ? 'bg-purple-100 text-purple-700' :
                        entry.status === 'absent' || entry.status === 'annual_leave' ? 'bg-gray-100 text-gray-700' :
                        entry.status === 'scheduled' || entry.status === 'not_started' ? 'bg-slate-100 text-slate-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.status === 'in_progress' ? 'In Progress' :
                         entry.status === 'late_in_progress' ? 'Late, In Progress' :
                         entry.status === 'finished' ? 'Finished' :
                         entry.status === 'finished_early' ? 'Finished Early' :
                         entry.status === 'finished_late' ? 'Finished Late' :
                         entry.status === 'missing' ? 'Missing' :
                         entry.status === 'sick' ? 'Sick' :
                         entry.status === 'annual_leave' ? 'Annual Leave' :
                         entry.status === 'absent' ? 'Absent' :
                         entry.status === 'scheduled' ? 'Scheduled' :
                         entry.status === 'not_started' ? 'Not Started' :
                         entry.status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">{entry.scheduled_hours || '0.00'}h</td>
                    <td className="px-2 py-2 text-right">{entry.raw_hours || '0.00'}h</td>
                    <td className="px-2 py-2 text-right">{entry.paid_breaks || '0.50'}h</td>
                    <td className="px-2 py-2 text-right">{entry.unpaid_breaks || '0.50'}h</td>
                    <td className="px-2 py-2 text-right font-semibold">{entry.actual_worked_hours || '0.00'}h</td>
                    <td className="px-2 py-2 text-right text-blue-600 font-semibold">{entry.overtime || '0.00'}h</td>
                    {!isFinance && activeTab === 'todo' && (
                      <td className="px-2 py-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedTimesheet(timesheet);
                            setEditingEntry({
                              date: entry.date,
                              actual_clock_in: entry.actual_clock_in || '',
                              actual_clock_out: entry.actual_clock_out || ''
                            });
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Weekly Summary */}
          {timesheet.weekly_summary && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-3">Weekly Summary</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                <div>
                  <p className="text-gray-600 text-xs">Contracted Hours</p>
                  <p className="font-bold text-lg">{timesheet.weekly_summary.contracted_hours}h</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Worked Hours</p>
                  <p className="font-bold text-lg text-green-600">{timesheet.weekly_summary.actual_worked_hours}h</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Overtime</p>
                  <p className="font-bold text-lg text-blue-600">{timesheet.weekly_summary.overtime}h</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Paid Breaks</p>
                  <p className="font-bold text-lg">{timesheet.weekly_summary.paid_breaks}h</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Unpaid Breaks</p>
                  <p className="font-bold text-lg">{timesheet.weekly_summary.unpaid_breaks}h</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Raw Hours</p>
                  <p className="font-bold text-lg">{timesheet.weekly_summary.raw_hours}h</p>
                </div>
              </div>
            </div>
          )}

          {/* TOIL Information */}
          {(timesheet.overtime_paid > 0 || timesheet.overtime_toil > 0 || timesheet.toil_used > 0) && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-3 text-purple-900">TOIL Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                {timesheet.toil_used > 0 && (
                  <div className="bg-white p-3 rounded-lg border border-purple-200">
                    <p className="text-gray-600 text-xs mb-1">TOIL Used</p>
                    <p className="font-bold text-lg text-purple-600">{timesheet.toil_used.toFixed(2)}h</p>
                    <p className="text-xs text-purple-700 mt-1">Applied to top up hours</p>
                  </div>
                )}
                {timesheet.overtime_paid > 0 && (
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <p className="text-gray-600 text-xs mb-1">Overtime Paid</p>
                    <p className="font-bold text-lg text-green-600">{timesheet.overtime_paid.toFixed(2)}h</p>
                    <p className="text-xs text-green-700 mt-1">To be paid in payroll</p>
                  </div>
                )}
                {timesheet.overtime_toil > 0 && (
                  <div className="bg-white p-3 rounded-lg border border-amber-200">
                    <p className="text-gray-600 text-xs mb-1">Overtime Banked</p>
                    <p className="font-bold text-lg text-amber-600">{timesheet.overtime_toil.toFixed(2)}h</p>
                    <p className="text-xs text-amber-700 mt-1">Added to TOIL balance</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isFinance ? 'Finance - Drivers Timesheets' : 'Drivers - Manage Timesheets'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isFinance ? 'Review and approve timesheets' : 'Review schedules and submit to finance'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {!isFinance && (
          <button
            onClick={() => {
              setActiveTab('active');
              setSelectedTimesheets([]);
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === 'active'
                ? 'border-b-2 border-emerald-600 text-emerald-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Active ({activeItems.length})
          </button>
        )}
        <button
          onClick={() => {
            setActiveTab('todo');
            setSelectedTimesheets([]);
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'todo'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {isFinance ? 'To-Do' : 'Submitted'} ({todoItems.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('queried');
            setSelectedTimesheets([]);
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'queried'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Queried ({queriedItems.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('approved');
            setSelectedTimesheets([]);
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'approved'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Approved ({approvedItems.length})
        </button>
      </div>

      {/* Date Filter for Approved Tab */}
      {activeTab === 'approved' && (
        <div className="mt-4">
          <DateFilter onFilterChange={handleDateFilterChange} />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'active' && !isFinance && (
            <>
              {activeItems.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No active timesheets</p>
                  <p className="text-sm text-gray-500 mt-2">Generate timesheets to see them here</p>
                </div>
              ) : (
                <>
                  {/* Bulk Actions Bar */}
                  <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedTimesheets.length === activeItems.length && activeItems.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-600">
                        {selectedTimesheets.length > 0 ? (
                          <strong>{selectedTimesheets.length} selected</strong>
                        ) : (
                          'Select all'
                        )}
                      </span>
                    </div>
                    {selectedTimesheets.length > 0 && (
                      <Button
                        onClick={handleBulkDelete}
                        variant="outline"
                        className="border-red-600 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Delete Selected ({selectedTimesheets.length})
                      </Button>
                    )}
                  </div>
                  
                  {/* Timesheet Cards */}
                  {activeItems.map(item => renderTimesheetCard(item, false, false, true, true))}
                </>
              )}
            </>
          )}
          
          {activeTab === 'todo' && (
            <>
              {todoItems.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">{isFinance ? 'No items to review' : 'No submitted timesheets'}</p>
                </div>
              ) : (
                todoItems.map(item => 
                  // For managers: only show timesheets (no submit button), they're already submitted
                  // For finance: show timesheets with approve/reject actions
                  renderTimesheetCard(item, isFinance, false)
                )
              )}
            </>
          )}
          
          {activeTab === 'queried' && (
            <>
              {queriedItems.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No queried items</p>
                </div>
              ) : (
                queriedItems.map(item => renderTimesheetCard(item, isFinance, !isFinance))
              )}
            </>
          )}
          
          {activeTab === 'approved' && (
            <>
              {approvedItems.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No approved items yet</p>
                </div>
              ) : (
                approvedItems.map(item => renderTimesheetCard(item, false))
              )}
            </>
          )}
        </div>
      )}

      {/* Edit Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Clock Times</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Date</Label>
                <Input value={new Date(editingEntry.date).toLocaleDateString()} disabled />
              </div>
              <div>
                <Label>Clock In Time</Label>
                <Input
                  type="time"
                  value={editingEntry.actual_clock_in}
                  onChange={(e) => setEditingEntry({ ...editingEntry, actual_clock_in: e.target.value })}
                />
              </div>
              <div>
                <Label>Clock Out Time</Label>
                <Input
                  type="time"
                  value={editingEntry.actual_clock_out}
                  onChange={(e) => setEditingEntry({ ...editingEntry, actual_clock_out: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditEntry} className="bg-emerald-600 hover:bg-emerald-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse Day Edit Dialog */}
      <Dialog open={showWarehouseDayEdit} onOpenChange={setShowWarehouseDayEdit}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Edit Day - {editingWarehouseDay?.date}</DialogTitle>
          </DialogHeader>
          {editingWarehouseDay && (
            <div className="space-y-4 py-4">
              {/* Clock Times */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_clock_in">Clock In</Label>
                  <input
                    id="edit_clock_in"
                    type="time"
                    value={editingWarehouseDay.clock_in}
                    onChange={(e) => setEditingWarehouseDay({ ...editingWarehouseDay, clock_in: e.target.value })}
                    className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_clock_out">Clock Out</Label>
                  <input
                    id="edit_clock_out"
                    type="time"
                    value={editingWarehouseDay.clock_out}
                    onChange={(e) => setEditingWarehouseDay({ ...editingWarehouseDay, clock_out: e.target.value })}
                    className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Break Info for Drivers */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Break Rule:</strong> If worked hours exceed 9.5 hours, a 15-minute break is automatically deducted.
                </p>
              </div>

              {/* Current Calculations Info */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Worked Hours:</span>
                  <span className="font-medium">{editingWarehouseDay.worked_hours}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Break Deduction:</span>
                  <span className="font-medium text-red-600">
                    {editingWarehouseDay.worked_hours > 9.5 ? '-0.25h' : 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Payable Hours:</span>
                  <span className="font-medium text-green-600">{editingWarehouseDay.payable_hours}h</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Note: Hours will be recalculated when you save changes
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWarehouseDayEdit(false)}>Cancel</Button>
            <Button onClick={handleEditWarehouseDay} className="bg-teal-600 hover:bg-teal-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse Approval Dialog */}
      <Dialog open={showWarehouseApproval} onOpenChange={setShowWarehouseApproval}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Approve Warehouse Timesheet</DialogTitle>
          </DialogHeader>
          {approvingTimesheet && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Employee:</span>
                  <span className="text-sm">{approvingTimesheet.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Period:</span>
                  <span className="text-sm">
                    {new Date(approvingTimesheet.period_start).toLocaleDateString('en-GB')} - {new Date(approvingTimesheet.period_end).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Payable Hours:</span>
                  <span className="text-sm font-bold text-green-600">{approvingTimesheet.total_payable_hours || 0}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Contracted Hours:</span>
                  <span className="text-sm">{approvingTimesheet.contracted_hours || 0}h</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium">Overtime:</span>
                  <span className="text-sm font-bold text-blue-600">{approvingTimesheet.overtime_hours || 0}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Current TOIL Balance:</span>
                  <span className="text-sm font-bold text-purple-600">{approvingTimesheet.toil_balance || 0}h</span>
                </div>
              </div>

              {/* Overtime Type Selection */}
              {approvingTimesheet.overtime_hours > 0 && (
                <div>
                  <Label className="text-sm font-semibold">Overtime Payment Type</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="overtime_toil"
                        name="overtime_type"
                        value="toil"
                        checked={approvalSettings.overtime_type === 'toil'}
                        onChange={(e) => setApprovalSettings({ ...approvalSettings, overtime_type: e.target.value })}
                        className="w-4 h-4 text-teal-600"
                      />
                      <label htmlFor="overtime_toil" className="text-sm">
                        <span className="font-medium">TOIL</span> - Add {approvingTimesheet.overtime_hours}h to TOIL balance
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="overtime_paid"
                        name="overtime_type"
                        value="paid"
                        checked={approvalSettings.overtime_type === 'paid'}
                        onChange={(e) => setApprovalSettings({ ...approvalSettings, overtime_type: e.target.value })}
                        className="w-4 h-4 text-teal-600"
                      />
                      <label htmlFor="overtime_paid" className="text-sm">
                        <span className="font-medium">Paid</span> - Include {approvingTimesheet.overtime_hours}h in payroll
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* TOIL Usage */}
              {approvingTimesheet.toil_balance > 0 && approvingTimesheet.total_payable_hours < approvingTimesheet.contracted_hours && (
                <div>
                  <Label htmlFor="toil_to_use" className="text-sm font-semibold">Use TOIL to Bump Hours</Label>
                  <p className="text-xs text-gray-600 mt-1 mb-2">
                    Shortfall: {(approvingTimesheet.contracted_hours - approvingTimesheet.total_payable_hours).toFixed(2)}h
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      id="toil_to_use"
                      type="number"
                      min="0"
                      max={Math.min(
                        approvingTimesheet.toil_balance,
                        approvingTimesheet.contracted_hours - approvingTimesheet.total_payable_hours
                      )}
                      step="0.5"
                      value={approvalSettings.toil_to_use}
                      onChange={(e) => setApprovalSettings({ ...approvalSettings, toil_to_use: parseFloat(e.target.value) || 0 })}
                      className="w-24 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm">hours from TOIL balance</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    New payable hours: {(approvingTimesheet.total_payable_hours + approvalSettings.toil_to_use).toFixed(2)}h
                  </p>
                </div>
              )}

              {/* Result Summary */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">After Approval:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Final Payable Hours:</span>
                    <span className="font-bold text-green-600">
                      {(approvingTimesheet.total_payable_hours + approvalSettings.toil_to_use).toFixed(2)}h
                    </span>
                  </div>
                  {approvingTimesheet.overtime_hours > 0 && (
                    <div className="flex justify-between">
                      <span>New TOIL Balance:</span>
                      <span className="font-bold text-purple-600">
                        {approvalSettings.overtime_type === 'toil' 
                          ? (approvingTimesheet.toil_balance + approvingTimesheet.overtime_hours - approvalSettings.toil_to_use).toFixed(2)
                          : (approvingTimesheet.toil_balance - approvalSettings.toil_to_use).toFixed(2)
                        }h
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWarehouseApproval(false)}>Cancel</Button>
            <Button onClick={handleWarehouseApprove} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={showEditHistory} onOpenChange={setShowEditHistory}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {editHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No edits made yet</p>
            ) : (
              editHistory.map((edit, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-sm">
                        {new Date(edit.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(edit.timestamp).toLocaleString('en-GB')}
                      </p>
                    </div>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {edit.edited_by}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {/* Clock In Changes */}
                    {edit.changes.before.clock_in !== edit.changes.after.clock_in && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Clock In:</span>
                        <span className="line-through text-red-600">{edit.changes.before.clock_in || '-'}</span>
                        <span>→</span>
                        <span className="text-green-600 font-medium">{edit.changes.after.clock_in || '-'}</span>
                      </div>
                    )}
                    
                    {/* Clock Out Changes */}
                    {edit.changes.before.clock_out !== edit.changes.after.clock_out && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Clock Out:</span>
                        <span className="line-through text-red-600">{edit.changes.before.clock_out || '-'}</span>
                        <span>→</span>
                        <span className="text-green-600 font-medium">{edit.changes.after.clock_out || '-'}</span>
                      </div>
                    )}
                    
                    {/* Break Changes */}
                    {JSON.stringify(edit.changes.before.breaks) !== JSON.stringify(edit.changes.after.breaks) && (
                      <div>
                        <span className="text-gray-600">Breaks:</span>
                        <div className="ml-4 mt-1 space-y-1">
                          {edit.changes.before.breaks.morning_break !== edit.changes.after.breaks.morning_break && (
                            <div className="text-xs">Morning: {edit.changes.after.breaks.morning_break ? '✓ Taken' : '✗ Not taken'}</div>
                          )}
                          {edit.changes.before.breaks.lunch_break !== edit.changes.after.breaks.lunch_break && (
                            <div className="text-xs">Lunch: {edit.changes.after.breaks.lunch_break ? '✓ Taken' : '✗ Not taken'}</div>
                          )}
                          {edit.changes.before.breaks.afternoon_break !== edit.changes.after.breaks.afternoon_break && (
                            <div className="text-xs">Afternoon: {edit.changes.after.breaks.afternoon_break ? '✓ Taken' : '✗ Not taken'}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowEditHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit to Finance Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Submit Timesheet to Finance</DialogTitle>
          </DialogHeader>
          
          {submittingTimesheet && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">{submittingTimesheet.employee_name}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {new Date(submittingTimesheet.period_start).toLocaleDateString()} - {new Date(submittingTimesheet.period_end).toLocaleDateString()}
                </p>
                <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                  <div>
                    <p className="text-gray-600 text-xs">Worked</p>
                    <p className="font-bold">{submittingTimesheet.total_worked_hours || 0}h</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Payable</p>
                    <p className="font-bold text-green-600">{submittingTimesheet.total_payable_hours || 0}h</p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs">Overtime</p>
                    <p className="font-bold text-orange-600">{submittingTimesheet.overtime_hours || 0}h</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">How should overtime be handled?</h4>
                <p className="text-xs text-gray-600 mb-4">
                  Split the {submittingTimesheet.overtime_hours || 0} hours of overtime between paid and TOIL
                </p>

                <div className="space-y-3">
                  <div>
                    <Label htmlFor="overtime_paid">Paid Overtime (hours)</Label>
                    <Input
                      id="overtime_paid"
                      type="number"
                      step="0.5"
                      min="0"
                      max={submittingTimesheet.overtime_hours || 0}
                      value={overtimeSplit.paid}
                      onChange={(e) => {
                        const paid = parseFloat(e.target.value) || 0;
                        const remaining = (submittingTimesheet.overtime_hours || 0) - paid;
                        setOvertimeSplit({ paid, toil: Math.max(0, remaining) });
                      }}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="overtime_toil">TOIL (Time Off In Lieu) - hours</Label>
                    <Input
                      id="overtime_toil"
                      type="number"
                      step="0.5"
                      min="0"
                      max={submittingTimesheet.overtime_hours || 0}
                      value={overtimeSplit.toil}
                      onChange={(e) => {
                        const toil = parseFloat(e.target.value) || 0;
                        const remaining = (submittingTimesheet.overtime_hours || 0) - toil;
                        setOvertimeSplit({ toil, paid: Math.max(0, remaining) });
                      }}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      TOIL will be added to employee's balance and can be used as paid time off
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total split:</span>
                      <span className="font-bold">{(overtimeSplit.paid + overtimeSplit.toil).toFixed(2)}h</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">Should equal:</span>
                      <span className="font-bold">{(submittingTimesheet.overtime_hours || 0).toFixed(2)}h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowSubmitDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleSubmitWarehouseTimesheet} className="bg-emerald-600 hover:bg-emerald-700">
              <Send className="w-4 h-4 mr-2" />
              Submit to Finance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversTimesheetsManager;
